import * as THREE from 'three';
import { camera } from './scene';
import { elStage } from '../platform/dom';
import { input } from '../sim/input';

/* ============================================================
   自由視角 —— 像 3D 編輯器一樣飛到處看地圖
   ============================================================

   使用者 2026-08-04：「幫我在 debug console 加一個上帝視角，
   讓我可以像在 3D 編輯器一樣到處飛行看地圖」。

   ## 三個「只有這樣做才成立」的地方

   1. **接管鏡頭要在 `updateCamera` 之前擋掉，不是之後覆蓋。**
      戰場鏡頭每幀都會把 `camera.position` 寫死在玩家身上，
      飛行模式如果只是「之後再設一次」，兩者會互相打架（畫面抖動）。
      所以 `game.ts` 是 `if (!flyActive()) updateCamera(...)`。

   2. **輸入要凍結，而且凍結在 `input` 物件上。**
      WASD 同時是機體的移動鍵 —— 不凍結的話一邊飛一邊把機體開進牆裡。
      旗標放在 `sim/input.ts` 的 `input.freeze`（模擬層自己的欄位），
      而不是讓 `sim/` 去 import `render/` —— 那個方向的相依會把模擬層
      綁死在瀏覽器上（`tools/lint-sim.js` 守的就是這件事）。

   3. **模擬不暫停。** 空的場地看不出問題，要看的是「三百隻在場上跑的時候
      這張圖長什麼樣」。想定格的話主控台還有 `pause`。

   ⚠️ 這個檔**只跑在畫面幀上**，不寫任何進入 `snapshot()` 的欄位，
   所以它不影響決定性，也不會被錄影／重播讀到。 */

var on = false;
var pos = new THREE.Vector3();
var yaw = 0, pitch = -0.6;
/** 基準速度（公尺／秒）。滾輪調，Shift ×3.4、Ctrl ×0.28。 */
var speed = 42;
var dragging = false, lastX = 0, lastY = 0;
var keys: Record<string, boolean> = {};
var hint: HTMLDivElement | null = null;
var wired = false;

export function flyActive() { return on; }

/** 切換自由視角。回傳給主控台印的那一行字。 */
export function toggleFly(force?: boolean) {
  var want = force === undefined ? !on : force;
  if (want === on) return on ? '自由視角：已經開著' : '自由視角：已經關著';
  on = want;
  (input as any).freeze = on;
  if (on) {
    wire();
    /* 從**現在的鏡頭**接手，不要跳到一個預設點 —— 開啟的瞬間畫面不動，
       才讀得出「我接管了這台鏡頭」而不是「畫面切走了」。 */
    pos.copy(camera.position);
    var e = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
    yaw = e.y; pitch = e.x;
    showHint(true);
    return '自由視角 ON —— WASD 平移／QE 升降／拖曳滑鼠轉向／Shift 加速／Ctrl 慢速／滾輪調速度。再輸入一次 fly 關閉';
  }
  keys = {}; dragging = false;
  showHint(false);
  return '自由視角 OFF —— 鏡頭還給戰場';
}

function wire() {
  if (wired) return; wired = true;
  /* ⚠️ 用 capture 並且**只在飛行時**吃事件：關掉之後所有按鍵要原封不動
     回到遊戲手上，所以這裡不做任何 preventDefault 以外的攔截。 */
  window.addEventListener('keydown', function (e) {
    if (!on) return;
    var tn = (e.target as any) && (e.target as any).tagName;
    if (tn === 'INPUT' || tn === 'TEXTAREA') return;   // 主控台打字時不要飛
    keys[e.key.toLowerCase()] = true;
    if ('wasdqe '.indexOf(e.key.toLowerCase()) >= 0) e.preventDefault();
  }, true);
  window.addEventListener('keyup', function (e) { keys[e.key.toLowerCase()] = false; }, true);
  window.addEventListener('blur', function () { keys = {}; dragging = false; });
  elStage.addEventListener('mousedown', function (e) {
    if (!on) return;
    dragging = true; lastX = e.clientX; lastY = e.clientY; e.preventDefault();
  }, true);
  window.addEventListener('mouseup', function () { dragging = false; }, true);
  window.addEventListener('mousemove', function (e) {
    if (!on || !dragging) return;
    yaw -= (e.clientX - lastX) * 0.0032;
    pitch -= (e.clientY - lastY) * 0.0032;
    /* 夾在 ±88°：翻過頭的話 YXZ 尤拉角會翻轉，畫面會突然上下顛倒 */
    pitch = Math.max(-1.53, Math.min(1.53, pitch));
    lastX = e.clientX; lastY = e.clientY;
  }, true);
  window.addEventListener('wheel', function (e) {
    if (!on) return;
    speed = Math.max(4, Math.min(400, speed * (e.deltaY > 0 ? 0.86 : 1.16)));
    if (hint) hint.textContent = hintText();
  }, { passive: true });
}

function hintText() {
  return '自由視角　WASD 平移 · QE 升降 · 拖曳滑鼠轉向 · Shift 加速 / Ctrl 慢速 · 滾輪調速度（'
    + speed.toFixed(0) + ' m/s）　·　高度 ' + pos.y.toFixed(0) + ' m　·　`fly` 關閉';
}

function showHint(v: boolean) {
  if (typeof document === 'undefined') return;
  if (v && !hint) {
    hint = document.createElement('div');
    hint.style.cssText = 'position:fixed;left:50%;bottom:14px;transform:translateX(-50%);z-index:60;'
      + 'font:11px/1.6 ui-monospace,monospace;letter-spacing:.06em;color:#cfe3ff;'
      + 'background:rgba(6,10,16,.72);border:1px solid rgba(120,180,255,.28);padding:5px 12px;'
      + 'border-radius:3px;pointer-events:none;white-space:nowrap';
    document.body.appendChild(hint);
  }
  if (hint) hint.style.display = v ? 'block' : 'none';
}

/** 直接把鏡頭放到某個位置與朝向（給截圖工具用：拖曳滑鼠在自動化裡不可重現）。
    `yaw` 0 ＝ 看向 −Z，`pitch` 負值 ＝ 往下看。 */
export function flyTo(x, y, z, yawR, pitchR) {
  toggleFly(true);
  pos.set(x, y, z);
  if (yawR !== undefined) yaw = yawR;
  if (pitchR !== undefined) pitch = Math.max(-1.53, Math.min(1.53, pitchR));
  updateFly(0);
  return { x: x, y: y, z: z, yaw: yaw, pitch: pitch };
}

/** 每幀。`dt` 是**真實時間**（跟遊戲的 timeScale 無關 —— 頓幀時鏡頭還是要跟手）。 */
export function updateFly(dt: number) {
  if (!on) return;
  var f = speed * dt * (keys['shift'] ? 3.4 : 1) * (keys['control'] ? 0.28 : 1);
  var sy = Math.sin(yaw), cy = Math.cos(yaw), sp = Math.sin(pitch), cp = Math.cos(pitch);
  /* 前進方向吃 pitch（看哪飛哪，跟編輯器一致）；左右嚴格水平，
     不然橫移會在斜視時把鏡頭帶上帶下，很難對準要看的東西。 */
  var fx = -sy * cp, fy = sp, fz = -cy * cp;
  var rx = cy, rz = -sy;
  var mv = 0;
  if (keys['w']) { pos.x += fx * f; pos.y += fy * f; pos.z += fz * f; mv = 1; }
  if (keys['s']) { pos.x -= fx * f; pos.y -= fy * f; pos.z -= fz * f; mv = 1; }
  if (keys['d']) { pos.x += rx * f; pos.z += rz * f; mv = 1; }
  if (keys['a']) { pos.x -= rx * f; pos.z -= rz * f; mv = 1; }
  if (keys['e']) { pos.y += f; mv = 1; }
  if (keys['q']) { pos.y -= f; mv = 1; }
  /* 不准鑽到地面以下：地面是單面的，穿過去只會看到一片黑而且很難飛回來 */
  if (pos.y < 1.5) pos.y = 1.5;
  camera.position.copy(pos);
  camera.quaternion.setFromEuler(new THREE.Euler(pitch, yaw, 0, 'YXZ'));
  camera.updateMatrixWorld();
  if (mv && hint) hint.textContent = hintText();
}
